import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { apiClient, formatCurrency } from '@/lib';

// BES-7448 / BES-7449: credit-card payment authorization e-sign step shown before
// the payment screen for gated hosts. The guest types their card descriptors, reads
// the rendered authorization, and signs. The text itself is rendered server-side
// (single source of truth) via /cc-auth/preview; this component only collects input.
export type CcAuthType = 'charge' | 'deposit';

interface CcAuthModalProps {
  authType: CcAuthType;
  amount: number;
  currency: string;
  context?: Record<string, unknown>;
  onAuthorized: () => void;
  onCancel: () => void;
}

const CARD_TYPES = ['Visa', 'Mastercard', 'American Express', 'Discover', 'Other'];

export function CcAuthModal({ authType, amount, currency, context, onAuthorized, onCancel }: CcAuthModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'details' | 'sign'>('details');
  const [bankName, setBankName] = useState('');
  const [cardType, setCardType] = useState('');
  const [cardLast4, setCardLast4] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [authorizationText, setAuthorizationText] = useState('');
  const [signedAt, setSignedAt] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const detailsValid =
    bankName.trim().length > 0 &&
    cardType.length > 0 &&
    /^\d{4}$/.test(cardLast4) &&
    /^(0[1-9]|1[0-2])\/\d{4}$/.test(cardExpiry);

  // Flag the expiry field red only when it holds a value that can never become
  // valid (bad first digit, month > 12, or a complete-but-malformed MM/YYYY) so we
  // don't outline a field the guest is still mid-way through typing.
  const expiryHasError = (() => {
    if (cardExpiry.length === 0) return false;
    const digits = cardExpiry.replace(/\D/g, '');
    if (digits.length >= 1 && digits[0] !== '0' && digits[0] !== '1') return true;
    if (digits.length >= 2) {
      const month = Number(digits.slice(0, 2));
      if (month < 1 || month > 12) return true;
    }
    if (cardExpiry.length === 7) return !/^(0[1-9]|1[0-2])\/\d{4}$/.test(cardExpiry);
    return false;
  })();

  const buildBody = () => ({
    authType,
    amount,
    currency,
    bankName: bankName.trim(),
    cardType,
    cardLast4: cardLast4.trim(),
    cardExpiry: cardExpiry.trim(),
  });

  const handleContinue = async () => {
    if (!detailsValid) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.post<{ authorizationText: string; signedAt: number }>(
        '/cc-auth/preview',
        buildBody()
      );
      setAuthorizationText(res.authorizationText);
      setSignedAt(res.signedAt);
      setStep('sign');
    } catch {
      setError(t('Unable to load the authorization. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  // Signature pad (dependency-free canvas + pointer events).
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    if (step !== 'sign') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Resizing the canvas wipes any prior drawing, so reset the signed flag too
    // (guest may have signed, gone Back, edited card details, and returned).
    setHasSignature(false);
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#111827';
    }
  }, [step]);

  const pointFromEvent = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pointFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvasRef.current?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointFromEvent(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!hasSignature) setHasSignature(true);
  };

  const handlePointerUp = () => {
    drawing.current = false;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const handleAgree = async () => {
    const canvas = canvasRef.current;
    if (!canvas || !hasSignature) return;
    try {
      setLoading(true);
      setError(null);
      await apiClient.post('/cc-auth', {
        ...buildBody(),
        // Pass back the preview's signing timestamp so the server re-renders the
        // identical text it showed (no client-supplied legal copy is trusted).
        signedAt,
        signatureBase64: canvas.toDataURL('image/png'),
        ...(context ? { context } : {}),
      });
      onAuthorized();
    } catch {
      setError(t('Unable to submit your authorization. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const title = authType === 'deposit' ? t('Authorize your security deposit') : t('Authorize your payment');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg border border-gray-200 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 p-5">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onCancel}
            className="text-gray-400 transition-colors hover:text-gray-600"
            aria-label={t('Close')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {step === 'details' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                {t('Enter your card details to authorize a payment of {{amount}}.', {
                  amount: formatCurrency(amount, currency),
                })}
              </p>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('Bank name')}</label>
                <input
                  type="text"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                  placeholder={t('e.g. Chase')}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t('Card type')}</label>
                <select
                  value={cardType}
                  onChange={(e) => setCardType(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">{t('Select card type')}</option>
                  {CARD_TYPES.map((ct) => (
                    <option key={ct} value={ct}>
                      {ct}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('Last 4 digits')}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={4}
                    value={cardLast4}
                    onChange={(e) => setCardLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="1234"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="mb-1 block text-sm font-medium text-gray-700">{t('Expiry (MM/YYYY)')}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={7}
                    value={cardExpiry}
                    aria-invalid={expiryHasError}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^\d]/g, '').slice(0, 6);
                      if (v.length >= 3) v = `${v.slice(0, 2)}/${v.slice(2)}`;
                      setCardExpiry(v);
                    }}
                    placeholder="MM/YYYY"
                    className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                      expiryHasError
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                    }`}
                  />
                  {expiryHasError && (
                    <p className="mt-1 text-xs text-red-600">{t('Enter a valid month and year (MM/YYYY).')}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === 'sign' && (
            <div className="space-y-4">
              <div className="max-h-64 overflow-y-auto whitespace-pre-line rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm leading-relaxed text-gray-800">
                {authorizationText}
              </div>

              <div>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">{t('Sign here')}</label>
                  <button
                    type="button"
                    onClick={clearSignature}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                  >
                    {t('Clear')}
                  </button>
                </div>
                <canvas
                  ref={canvasRef}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerLeave={handlePointerUp}
                  className="h-40 w-full touch-none rounded-lg border border-gray-300 bg-white"
                />
              </div>
            </div>
          )}

          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
          {step === 'details' ? (
            <>
              <Button variant="outline" onClick={onCancel} disabled={loading}>
                {t('Cancel')}
              </Button>
              <Button variant="primary" onClick={handleContinue} disabled={!detailsValid || loading}>
                {loading ? t('Loading...') : t('Continue')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep('details')} disabled={loading}>
                {t('Back')}
              </Button>
              <Button variant="primary" onClick={handleAgree} disabled={!hasSignature || loading}>
                {loading ? t('Submitting...') : t('Agree & continue')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
